import React, { useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { TailwindModal } from '~/libs/ui/atoms/TailwindModal';

import type {
	ErpCountryWithPriceTypeResponse,
	ErpScenarioResponse,
	ErpSessionDetailResponse,
} from 'com.cogvio.pricemonitor.api';

import { Country } from '~/Api/Country';
import { ModalPriceDotLegend } from './ModalPriceDotLegend';
import { InterventionModalRow, InterventionState } from './InterventionScenarioModalRow';
import { ButtonCancel } from './ButtonCancel';
import { ButtonSave } from './ButtonSave';
import { ERPInterventionEditData } from '~/libs/infrastructure/schemas/erp';
import { sortCountries } from '../store/sortingContext';

const NO_MONTHS = 4 * 12;

export interface InterventionScenarioModalProps {
	currency: string;
	erpCountriesResult: ErpCountryWithPriceTypeResponse[];
	erpDate: string;
	erpSessionResult: ErpSessionDetailResponse;
	scenario?: ErpScenarioResponse | null;
	userId: string;
	loading: boolean;
	errorScenarioModal: string;
	onClose: () => void;
	onSubmit: (
		scenario: ErpScenarioResponse | null | undefined,
		scenarioEditData: ERPInterventionEditData[],
		nextScenarioName: string
	) => void;
}

export function InterventionScenarioModal({
	currency,
	erpCountriesResult,
	erpDate,
	erpSessionResult,
	scenario,
	userId,
	loading,
	errorScenarioModal,
	onClose,
	onSubmit,
}: InterventionScenarioModalProps) {
	const [nameErrors, setNameErrors] = useState<Set<string>>(new Set());
	const [scenarioName, setScenarioName] = useState<string>(scenario?.name || '');
	const nameInputRef = useRef<HTMLInputElement | null>(null);
	const [interventionState, setInterventionState] = useState<InterventionState>(
		(scenario?.interventions || []).reduce(
			(all: InterventionState, data) => ({
				...all,
				[data.country.alpha2]: (all[data.country.alpha2] || []).concat([
					{
						type: data.type,
						month: data.month as unknown as string,
						price: data.price
							? {
									...data.price,
									value: typeof data.price.value === 'number' ? data.price.value.toFixed(2) : data.price.value,
							  }
							: null,
					},
				]),
			}),
			{}
		)
	);
	const months = useMemo(
		() =>
			Array.from(Array(NO_MONTHS)).map((_, index) => {
				const date = moment().startOf('month');

				return date.add(index + 1, 'months');
			}),
		[]
	);

	const hasNameError = nameErrors.size > 0 || scenarioName.length === 0;
	const addingScenario = !scenario;

	const onModalSubmit = (nextScenarioName: string) => {
		const interventionStateEntries = Object.entries(interventionState);
		const hasInputErrors = interventionStateEntries.some(([_, data]) => data.find(({ error }) => Boolean(error)));

		if (hasNameError || hasInputErrors || !userId) {
			if (hasNameError) {
				nameInputRef.current?.focus();
			}

			return;
		}

		const scenarioEditData = interventionStateEntries.reduce(
			(aggreagator: ERPInterventionEditData[], [countryCode, interventionData]) => [
				...aggreagator,
				...interventionData.map((countryData) => {
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					const { error, ...apiData } = countryData;

					return { countryCode, ...apiData };
				}),
			],
			[]
		);

		onSubmit(scenario, scenarioEditData, nextScenarioName);
	};

	return (
		<TailwindModal
			submitDisabled={loading}
			footer={
				<>
					<ButtonCancel onClick={onClose}>Close</ButtonCancel>
					<ButtonSave disabled={hasNameError} loading={loading}>
						Save &amp; execute model
					</ButtonSave>
				</>
			}
			title={addingScenario ? 'Add scenario' : 'Edit scenario'}
			onClose={onClose}
			onSubmit={() => {
				onModalSubmit(scenarioName);
			}}
		>
			<div className='px-3 pb-4 bg-white overflow-auto'>
				<div className='pb-4 flex items-center'>
					{addingScenario ? (
						<div className='flex flex-col'>
							<div className='flex flex-row items-start'>
								<h2 className='mr-2 pt-1.5 font-display text-label font-semibold text-cPurple-600 tracking-wider'>
									Name:
								</h2>
								<div className='w-full'>
									<input
										autoFocus
										className={`max-w-full w-72 px-2 py-1.5 font-display text-label text-black font-semibold leading-4 border  rounded-sm tabular-nums bg-white ${
											hasNameError || errorScenarioModal ? 'border-red-600 text-red-600' : 'border-gray-500'
										}`}
										defaultValue={scenarioName}
										disabled={loading}
										placeholder='A new scenario name'
										ref={nameInputRef}
										type='text'
										onChange={(event) => {
											setScenarioName(event.currentTarget.value);
											setNameErrors(new Set());
										}}
									/>
									{errorScenarioModal == 'erp-scenario-already-exists' && (
										<p className='mt-1.5 text-label text-left leading-tight font-semibold text-red-700 tracking-wide'>
											This name is already taken by some other scenario!
										</p>
									)}
								</div>
							</div>
						</div>
					) : (
						<span className='w-72 -mx-3 py-1.5 font-display text-black font-semibold leading-4'>{scenario?.name}</span>
					)}
				</div>
				<div className='-mx-3'>
					<table cellPadding='0' cellSpacing='0' width='100%'>
						<thead>
							<tr>
								<th className='pl-3 pr-2 py-1.5 text-left font-display text-label font-semibold text-gray-800 leading-none border-b-2 border-gray-300'>
									Country
								</th>
								<th className='pl-2 py-1.5 text-left font-display text-label font-semibold text-gray-800 leading-none border-b-2 border-gray-300'>
									Intervention
								</th>
								<th className='px-2 py-1.5 text-left font-display text-label font-semibold text-gray-800 leading-none border-b-2 border-gray-300'>
									Price [{currency}]
								</th>
								<th className='pl-2 pr-3 py-1.5 font-display text-label font-semibold text-gray-800 leading-none border-b-2 border-gray-300 whitespace-nowrap text-right'>
									Date
								</th>
							</tr>
						</thead>
						<tbody>
							{sortCountries(erpCountriesResult || []).map((country) => (
								<InterventionModalRow
									country={new Country(country.alpha2, country.name)}
									currency={currency}
									erpDate={erpDate}
									erpSessionResult={erpSessionResult}
									interventionState={interventionState}
									key={country.alpha2}
									months={months}
									onChange={setInterventionState}
								/>
							))}
						</tbody>
					</table>
				</div>
				<ModalPriceDotLegend />
			</div>
		</TailwindModal>
	);
}
